import React, { memo, useEffect } from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import { LoadScript, GoogleMap, InfoWindow, OverlayView } from '@react-google-maps/api';
import { priceFormattingSymbol } from '../../utils/utils';

const PriceMarker = ({ listing, onClick }) => {
  const theme = useTheme();
  return (
    <OverlayView
      position={listing.location}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <Box
        onClick={onClick}
        style={{
          position: 'absolute',
          left: '-50%',
          top: '-50%',
          backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#FFF',
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: '9999px',
          padding: '6px 16px',
          color: theme.palette.mode === 'dark' ? '#FFF' : theme.palette.primary.main,
          fontWeight: '700',
          fontSize: '14px',
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          boxShadow: theme.palette.mode === 'dark' ? '0 0 8px rgba(0,0,0,0.5)' : 'none'
        }}
      >
        {priceFormattingSymbol(listing?.price,listing?.country?.currencyCode)}
      </Box>
    </OverlayView>
  );
};

const getBounds = (google, listings = []) => {
  if (!listings.length) return null;
  const bounds = new google.maps.LatLngBounds();
  listings.forEach(listing => {
    bounds.extend(new google.maps.LatLng(
      listing.location.lat,
      listing.location.lng
    ));
  });
  return bounds;
};

const MapComponent = ({
  googleMapsApiKey,
  mapContainerStyle,
  selectedUniversity,
  defaultCenter,
  defaultZoom,
  universities = [],
  mapRef,
  onLoad,
  listings = [],
  selectedMarker,
  setSelectedMarker,
  handleMarkerClick
}) => {
  const theme = useTheme();

  useEffect(() => {
    if (!mapRef.current || !window.google) return;
    
    if (listings.length > 0) {
      const bounds = getBounds(window.google, listings);
      if (bounds) {
        mapRef.current.fitBounds(bounds);
      }
    } else if (selectedUniversity) {
      const uni = universities.find(u => u.collegeId === selectedUniversity);
      if (uni?.latitude && uni?.longitude) {
        mapRef.current.panTo({
          lat: parseFloat(uni.latitude),
          lng: parseFloat(uni.longitude)
        });
        mapRef.current.setZoom(13);
      }
    }
  }, [listings, selectedUniversity, universities, mapRef]);

  return (
    <Box sx={{ height: '100%', borderRadius: '10px', overflow: 'hidden' }}>
      <LoadScript googleMapsApiKey={googleMapsApiKey}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={selectedUniversity ? 13 : defaultZoom}
          center={selectedUniversity 
            ? {
                lat: parseFloat(universities.find(u => u.collegeId === selectedUniversity)?.latitude || defaultCenter.lat),
                lng: parseFloat(universities.find(u => u.collegeId === selectedUniversity)?.longitude || defaultCenter.lng)
              }
            : defaultCenter
          }
          onLoad={onLoad}
          options={{
            styles: [],
            fullscreenControl: false,
            streetViewControl: false,
            mapTypeControl: false,
            zoomControl: true
          }}
        >
          {listings.map((listing) => (
            <PriceMarker
              key={listing.id}
              listing={listing}
              onClick={() => {
                setSelectedMarker(listing);
                // handleMarkerClick(listing);
              }}
            />
          ))}

          {selectedMarker && (
            <InfoWindow
              position={selectedMarker.location}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <Box sx={{ p: 1, minWidth: '200px' }}>
              <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 1,
                    color: theme => theme.palette.text.secondary 
                  }}
                >
                  {selectedMarker.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: 'bold',
                    color: theme => theme.palette.primary.main
                  }}
                >
                  £{selectedMarker.price} / month
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  fullWidth
                  onClick={() => handleMarkerClick(selectedMarker)}
                >
                  View Details
                </Button>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>
    </Box>
  );
};

export default memo(MapComponent);